import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AppearanceColorKey, IAppearanceColor } from '@web/dtos';
import { SectionComponent } from '../layout/section/section.component';
import { SectionHeaderComponent } from '../layout/section/section-header/section-header.component';
import { CmsTextProviderService } from '../../services/cms-text-provider.service';
import { ICorporateDesignForm } from '../../entities/coporate-design-entity';
import { ColorPickerComponent } from '../data-input/color-picker/color-picker.component';
import { ColorPickerListComponent } from '../data-input/color-picker-list/color-picker-list';
import { ThemeComponent } from '../../theming';
import { CorporateDesignFormsFactory } from '../../factory/corporateDesign/corporateDesign-forms-factory.service';
import { ButtonComponent } from '../feedback/button/button.component';
import { TextInputComponent } from '../data-input/form-inputs/text-input/text-input.component';
import { CheckboxComponent } from '../data-input/form-inputs/checkbox/checkbox.component';
import { SingleSelectComponent } from '../data-input/form-inputs/single-select/single-select.component';
import { ToggleComponent } from '../data-input/form-inputs/toggle/toggle.component';
import { ButtonGroupComponent } from '../data-input/form-inputs/button-group/button-group.component';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'aw-appearance-standard-controls',
  imports: [
    NgClass,
    ReactiveFormsModule,
    NgTemplateOutlet,
    ColorPickerComponent,
    SectionComponent,
    SectionHeaderComponent,
    ColorPickerListComponent,
    ThemeComponent,
    ButtonComponent,
    TextInputComponent,
    CheckboxComponent,
    SingleSelectComponent,
    ToggleComponent,
    ButtonGroupComponent
  ],
  templateUrl: './appearance-standard-controls.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppearanceStandardControlsComponent {
  public readonly forceCollapse: InputSignal<boolean> = input(false);
  public readonly designForm: InputSignal<FormGroup<ICorporateDesignForm>> =
    input.required<FormGroup<ICorporateDesignForm>>();

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly designFactory: CorporateDesignFormsFactory = inject(CorporateDesignFormsFactory);
  protected readonly design = toSignal(
    toObservable(this.designForm).pipe(
      switchMap(form =>
        form.valueChanges.pipe(
          startWith(null),
          map(() => this.designFactory.buildEntity(form))
        )
      )
    )
  );
  protected form: FormGroup = new FormGroup({
     text: new FormControl<string>(this.cmsTextProvider.appearanceText.controlsInput),
     checkBox1: new FormControl<boolean>(true),
     checkBox2: new FormControl<boolean>(false),
     singleSelect1: new FormControl(1),
     singleSelect2: new FormControl(undefined),
     toggle1: new FormControl<boolean>(true),
     toggle2: new FormControl<boolean>(false),
     buttonControl: new FormControl<number>(1),
    });

  protected readonly colors: IAppearanceColor[] = [
    {
      key: 'fill',
      label: this.cmsTextProvider.appearanceText.controlsFillColour,
      description: this.cmsTextProvider.appearanceText.controlsFillColourHint,
    },
    {
      key: 'contrast',
      label: this.cmsTextProvider.appearanceText.controlsContrast,
    },
    {
      key: 'border',
      label: this.cmsTextProvider.appearanceText.controlsBorder,
      description: this.cmsTextProvider.appearanceText.controlsBorderHint,
    },
    {
      key: 'outline',
      label: this.cmsTextProvider.appearanceText.controlsOutline,
      description: this.cmsTextProvider.appearanceText.controlsOutlineHint,
    },
  ];

  protected swatchColor(key: AppearanceColorKey, isDark: boolean): string {
    switch (key) {
      case 'fill':
        return this.colorControl('fill', isDark).value;
      case 'contrast':
        return this.colorControl('contrast', isDark).value;
      case 'border':
        return this.colorControl('border', isDark).value;
      case 'outline':
        return this.colorControl('outline', isDark).value;
    }
  }

  protected colorControl(
    key: AppearanceColorKey,
    isDark: boolean
  ): FormControl<string> {
    switch (key) {
      case 'fill':
        return isDark
          ? this.designForm().controls.primaryDarkColor
          : this.designForm().controls.primaryColor;
      case 'contrast':
        return isDark
          ? this.designForm().controls.primaryDarkFontColor
          : this.designForm().controls.primaryFontColor;
      case 'border':
        return isDark
          ? this.designForm().controls.primaryDarkBorderColor
          : this.designForm().controls.primaryBorderColor;
      case 'outline':
        return isDark
          ? this.designForm().controls.primaryDarkAlternativeColor
          : this.designForm().controls.primaryAlternativeColor;
    }
  }

  protected selectColor(
    key: AppearanceColorKey,
    isDark: boolean,
    color: string
  ): void {
    this.colorControl(key, isDark).setValue(`#${color.replace(/^#/, '')}`);  
  }
}
